/*  =========================================================================
    src/Pages/InvoiceDetails.js  •  v2.4
    -------------------------------------------------------------------------
    - Inline Status dropdown (view mode) with auto-save + guard
    - Prefill selected Task on edit after tasks load
    - Supports pseudo-release “General Costs” (releaseId === null)
    ====================================================================== */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import pencilIcon from '../assets/icons/pencil.svg';
import trashIcon  from '../assets/icons/trash.svg';
import { api } from '../api';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

/* colour chip for payment status */
const getStatusBackground = (status) => {
  switch (status) {
    case 'paid':       return '#28a745';
    case 'processed':  return '#c376da';
    case 'unpaid':
    case 'needs_paid': return 'orange';
    default:           return '#555';
  }
};

/* synthetic release entry */
const GENERAL_RELEASE = { _id: 'null', title: 'General Costs' };

function InvoiceDetails({ invoices, setInvoices, projects = [], releases = [] }) {
  /* router / params */
  const { id: invoiceId } = useParams();
  const navigate          = useNavigate();

  const location = useLocation();
const navInvoice = location.state?.invoice || null;  // invoice passed from the table (fast path)

const [fetchedInvoice, setFetchedInvoice] = useState(null);
const [fetchingInvoice, setFetchingInvoice] = useState(false);
const [fetchErr, setFetchErr] = useState('');

  /* locate current invoice (prop list OR nav state OR fetched fallback) */
const invoiceFromList = invoices.find((inv) => String(inv._id) === String(invoiceId));
const invoice = invoiceFromList || navInvoice || fetchedInvoice;

  /* edit mode state */
  const [isEditing, setIsEditing] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState({
    description: '', amount: '', costType: '', paymentStatus: '',
  });

  /* inline status editor (view mode) */
  const [statusLocal, setStatusLocal]   = useState('unpaid');
  const [statusSaving, setStatusSaving] = useState(false);

  /* PDF replace */
  const [newFile, setNewFile]     = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [pdfBust, setPdfBust] = useState(0);

  /* Project / Release pickers */
  const [projectSearch,  setProjectSearch]  = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [releaseSearch,  setReleaseSearch]  = useState('');
  const [selectedRelease, setSelectedRelease] = useState(null);

  // Full releases index for the modal's release picker
const [releasesIndex, setReleasesIndex] = useState([]);

  /* Task picker */
  const [tasksForRelease, setTasksForRelease] = useState([]);
  const [taskSearch,      setTaskSearch]      = useState('');
  const [selectedTask,    setSelectedTask]    = useState(null);
  const [loadingTasks,    setLoadingTasks]    = useState(false);

  /* dropdown visibility flags */
  const [projFocus, setProjFocus] = useState(false);
  const [relFocus,  setRelFocus]  = useState(false);
  const [taskFocus, setTaskFocus] = useState(false);

  // Build a full releases index (paged) for reliable dropdown search
useEffect(() => {
  (async () => {
    try {
      // Backend caps /api/releases/paged limit to 100; keep client in sync
      // so we actually page through the full set.
      const pageSize = 100;
      let page = 1;
      let total = 0;
      const acc = [];
      do {
        const qs = new URLSearchParams({
          limit: String(pageSize),
          page: String(page),
          fields: 'title,projectId,coverArt',
        });
        const r = await api(`/api/releases/paged?${qs.toString()}`);
        if (!r.ok) break;
        const data = await r.json(); // { items, total }
        if (Array.isArray(data.items)) acc.push(...data.items);
        total = Number(data.total || 0);
        page += 1;
      } while ((page - 1) * pageSize < total);

      setReleasesIndex(acc);
    } catch {
      setReleasesIndex([]); // fall back to prop "releases" later
    }
  })();
}, []);

// Bust the iframe cache ONLY when the file name changes
useEffect(() => {
  if (invoice?.fileName) {
    setPdfBust(Date.now());
  }
}, [invoice?.fileName]);

  /* fetch tasks when release changes */
  useEffect(() => {
    if (!selectedProject || !selectedRelease) {
      setTasksForRelease([]); setTaskSearch(''); setSelectedTask(null);
      return;
    }
    (async () => {
      try {
        setLoadingTasks(true);
        const relParam = selectedRelease._id === 'null' ? 'null' : selectedRelease._id;
        const url = `/api/marketingtasks?projectId=${selectedProject._id}&releaseId=${relParam}`;
        const list = await api(url).then(r => r.json());
        setTasksForRelease(list);
      } catch (err) {
        console.error('[InvoiceDetails] load tasks error', err);
        setTasksForRelease([]);
      } finally { setLoadingTasks(false); }
    })();
  }, [selectedProject, selectedRelease]);

  // Fallback: fetch the invoice by id if not found in list or nav state
useEffect(() => {
  if (invoiceFromList || navInvoice || !invoiceId) return;

  let cancelled = false;
  (async () => {
    try {
      setFetchingInvoice(true);
      setFetchErr('');
      const r = await api(`/api/invoices/${invoiceId}`);
      if (!r.ok) throw new Error(`Fetch ${r.status}`);
      const doc = await r.json();
      if (!cancelled) setFetchedInvoice(doc);
    } catch (e) {
      if (!cancelled) setFetchErr(e.message || 'Failed to load invoice');
    } finally {
      if (!cancelled) setFetchingInvoice(false);
    }
  })();

  return () => { cancelled = true; };
}, [invoiceId, invoiceFromList, navInvoice]);

  /* Prefill selected Task once tasksForRelease arrive (edit mode) */
  useEffect(() => {
    if (!isEditing) return;
    if (!invoice?.taskId) return;
    if (!selectedProject || !selectedRelease) return;
    if (!tasksForRelease || tasksForRelease.length === 0) return;

    const currentTaskId =
      typeof invoice.taskId === 'object' ? invoice.taskId._id : invoice.taskId;

    const match = tasksForRelease.find(
      (t) => String(t._id) === String(currentTaskId)
    );

    if (match) {
      setSelectedTask(match);
      setTaskSearch(match.name || '');
    }
  }, [isEditing, invoice, tasksForRelease, selectedProject, selectedRelease]);

  /* keep inline status selector in sync with invoice */
  useEffect(() => {
    if (!invoice) return;
    const normalized = invoice.paymentStatus === 'needs_paid'
      ? 'unpaid'
      : (invoice.paymentStatus || 'unpaid');
    setStatusLocal(normalized);
  }, [invoice]);

  /* helpers (view mode titles) */
  const getProjectTitle = () => {
    if (!invoice?.projectId) return 'No Project';
    if (typeof invoice.projectId === 'object') return invoice.projectId.title || 'No Project';
    const found = projects.find((p) => String(p._id) === String(invoice.projectId));
    return found ? found.title : 'No Project';
  };
  const getReleaseTitle = () => {
    if (invoice?.releaseId === null) return 'General Costs';
    if (!invoice?.releaseId) return 'No Release';
    if (typeof invoice.releaseId === 'object') return invoice.releaseId.title || 'No Release';
    const found = releases.find((r) => String(r._id) === String(invoice.releaseId));
    return found ? found.title : 'No Release';
  };
  const getTaskName = () => {
    if (!invoice?.taskId) return 'No Task';
    if (typeof invoice.taskId === 'object') return invoice.taskId.name || 'No Task';
    const found = tasksForRelease.find((t) => String(t._id) === String(invoice.taskId));
    return found ? found.name : 'Task';
  };

  /* derived filtered lists ------------------------------------------------- */
  const recent = (arr) => [...arr].slice(-10).reverse();

  /* projects */
  const filteredProjects = projectSearch
    ? projects.filter((p) =>
        p.title?.toLowerCase().includes(projectSearch.toLowerCase()))
    : recent(projects);

/* releases (add General Costs) — use full index if available */
const sourceReleases =
  (Array.isArray(releasesIndex) && releasesIndex.length)
    ? releasesIndex
    : releases;

const releasesForProject = selectedProject
  ? sourceReleases.filter((r) => {
      const pid = typeof r.projectId === 'object' ? r.projectId._id : r.projectId;
      return String(pid) === String(selectedProject._id);
    })
  : sourceReleases;

const allReleases = [GENERAL_RELEASE, ...releasesForProject];

const filteredReleases = releaseSearch
  ? allReleases.filter((r) =>
      r.title?.toLowerCase().includes(releaseSearch.toLowerCase()))
  : recent(allReleases);

  /* tasks */
  const filteredTasks = taskSearch
    ? tasksForRelease.filter((t) =>
        t.name?.toLowerCase().includes(taskSearch.toLowerCase()))
    : recent(tasksForRelease);

  /* enter edit mode */
  const handleEdit = () => {
    if (!invoice) return;
    setIsEditing(true);

    setEditingInvoice({
      description: invoice.description || '',
      amount     : invoice.amount      || '',
      costType   : invoice.costType    || '',
      paymentStatus:
        invoice.paymentStatus === 'needs_paid' ? 'unpaid' : (invoice.paymentStatus || 'unpaid'),
    });

    if (invoice.projectId) {
      if (typeof invoice.projectId === 'object') {
        setSelectedProject(invoice.projectId);
        setProjectSearch(invoice.projectId.title || '');
      } else {
        const p = projects.find((pr) => pr._id === invoice.projectId);
        if (p) { setSelectedProject(p); setProjectSearch(p.title); }
      }
    }

    if (invoice.releaseId === null) {
      setSelectedRelease(GENERAL_RELEASE);
      setReleaseSearch(GENERAL_RELEASE.title);
    } else if (invoice.releaseId) {
      if (typeof invoice.releaseId === 'object') {
        setSelectedRelease(invoice.releaseId);
        setReleaseSearch(invoice.releaseId.title || '');
      } else {
        const r = releases.find((re) => re._id === invoice.releaseId);
        if (r) { setSelectedRelease(r); setReleaseSearch(r.title); }
      }
    }

    setNewFile(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedProject(null); setProjectSearch('');
    setSelectedRelease(null); setReleaseSearch('');
    setSelectedTask(null);   setTaskSearch('');
    setNewFile(null);
  };

  /* inline status update */
  const updateStatusInline = async (newVal) => {
    if (!invoice) return;
    if (statusSaving) return;
    setStatusSaving(true);
    try {
      const finalStatus = newVal === 'unpaid' ? 'needs_paid' : newVal;
      const r = await api(`/api/invoices/${invoice._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentStatus: finalStatus }),
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      const updated = await r.json();

      // keep the local detail record in sync immediately
setFetchedInvoice(prev => {
  // if we already fetched or have a placeholder, update it
  // otherwise keep null (invoiceFromList/navInvoice will cover it)
  if (!prev && !navInvoice) return updated;
  // if prev exists, merge
  return prev ? { ...prev, paymentStatus: updated.paymentStatus } : updated;
});

      setInvoices((prev) =>
        prev.map((inv) =>
          String(inv._id) === String(updated._id) ? updated : inv
        )
      );

      const normalized = updated.paymentStatus === 'needs_paid' ? 'unpaid' : updated.paymentStatus;
      setStatusLocal(normalized);
    } catch (err) {
      alert(err.message);
      const normalized = invoice.paymentStatus === 'needs_paid'
        ? 'unpaid'
        : (invoice.paymentStatus || 'unpaid');
      setStatusLocal(normalized);
    } finally {
      setStatusSaving(false);
    }
  };

  /* full save in edit mode */
  const saveEditedInvoice = async () => {
    if (!invoice) return;
    const finalStatus = editingInvoice.paymentStatus === 'unpaid'
      ? 'needs_paid' : editingInvoice.paymentStatus;

    const body = {
      description : editingInvoice.description,
      amount      : editingInvoice.amount,
      costType    : editingInvoice.costType,
      paymentStatus: finalStatus,
      projectId   : selectedProject ? selectedProject._id : null,
      releaseId   : selectedRelease
        ? (selectedRelease._id === 'null' ? null : selectedRelease._id)
        : null,
      taskId      : selectedTask   ? selectedTask._id    : null,
    };

    try {
      const r = await api(`/api/invoices/${invoice._id}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      const updated = await r.json();
      // update local detail view immediately
setFetchedInvoice(updated);
      setInvoices(prev =>
        prev.map((inv) => String(inv._id) === String(updated._id) ? updated : inv));
      setIsEditing(false);
    } catch (err) {
      alert(err.message);
      console.error(err);
    }
  };

  /* replace PDF */
  const handleNewFileChange = (e) => setNewFile(e.target.files[0] || null);

  const handleReplacePDF = async () => {
    if (!newFile || !invoice) { alert('Select a PDF first'); return; }
    setUploadingFile(true);
    try {
      const data = new FormData(); data.append('invoice', newFile);
      const up = await api('/api/invoiceupload/upload-pdf?skipExtraction=true', {
  method: 'POST',
  body: data,
});
      if (!up.ok) throw new Error('Upload error');
      const { fileName } = await up.json();
      if (!fileName) throw new Error('No fileName from server');

      const put = await api(`/api/invoices/${invoice._id}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ fileName }),
      });
      if (!put.ok) throw new Error(`PUT ${put.status}`);
      const updated = await put.json();
      setFetchedInvoice(updated); // keep detail view in sync now
setInvoices(prev =>
  prev.map(i => String(i._id) === String(updated._id) ? updated : i)
);
setNewFile(null);
alert('PDF replaced!');
    } catch (e) { alert(e.message); } finally { setUploadingFile(false); }
  };

  /* delete */
  const handleDelete = async () => {
    if (!invoice) return;
    if (!window.confirm('Delete this invoice?')) return;
    try {
      const r = await api(`/api/invoices/${invoice._id}`,{method:'DELETE'});
      if (!r.ok) throw new Error(`Status ${r.status}`);
      setInvoices(prev=>prev.filter(i=>String(i._id)!==String(invoice._id)));
      navigate('/invoices');
    } catch(e){alert(e.message);}
  };

  /* guard when invoice missing */
if (!invoice) {
  if (fetchingInvoice) {
    return (
      <div style={{ padding:20, color:'white' }}>
        <h2>Loading invoice…</h2>
      </div>
    );
  }
  return (
    <div style={{ padding:20, color:'white' }}>
      <h2>Invoice not found</h2>
      {fetchErr && <p style={{ color: 'salmon' }}>{fetchErr}</p>}
      <button
        onClick={()=>navigate('/invoices')}
        style={{
          marginTop:20, padding:'10px 20px', background:'#555', color:'#fff',
          border:'none', borderRadius:5, cursor:'pointer',
        }}
      >Go Back</button>
    </div>
  );
}

// Use this instead of navigate(-1) so back works even if there is no history entry
const safeBack = () => {
  if (window.history.length > 1) navigate(-1);
  else navigate('/invoices');
};

  /* ─────────────────────────── render ─────────────────────────── */
  return (
    <div style={{ padding:20, color:'white' }}>

      {/* back */}
      <button
        onClick={safeBack}
        style={{
          background:'transparent', border:'none', color:'#fff',
          fontSize:24, cursor:'pointer', marginBottom:20,
          display:'flex', alignItems:'center',
        }}
      >
        <svg width="32" height="32" viewBox="0 0 24 24"
              style={{ marginRight:8, fill:'#333' }}>
          <path d="M10 6 L8.59 7.41 13.17 12 l-4.58 4.59L10 18 l6-6z"
                transform="rotate(180,12,12)" />
        </svg>
      </button>

      <h1 style={{ marginBottom:20 }}>{invoice.description}</h1>

      {/* VIEW MODE */}
      {!isEditing ? (
        <div style={{
          background:'#1e1e1e', padding:20, borderRadius:10,
          width:500, maxWidth:'90vw',
        }}>
          <div style={{ display:'flex', alignItems:'center', marginBottom:10 }}>
            <h2 style={{ margin:0 }}>Release: {getReleaseTitle()}</h2>
            <img src={pencilIcon} alt="Edit"
                  onClick={handleEdit}
                  style={{ width:24, height:24, cursor:'pointer',
                          marginLeft:10, filter:'invert(20%)' }} />
          </div>

          <p style={{ margin:'5px 0' }}><strong>Project:</strong> {getProjectTitle()}</p>
          <p style={{ margin:'5px 0' }}><strong>Task:</strong> {getTaskName()}</p>
          <p style={{ margin:'5px 0' }}><strong>Amount:</strong> €{invoice.amount}</p>
          <p style={{ margin:'5px 0' }}><strong>Cost Type:</strong> {invoice.costType||'—'}</p>

          {/* INLINE STATUS (dropdown only, colored) */}
          <div style={{ margin:'5px 0' }}>
            <strong>Status:</strong>{' '}
            <select
              value={statusLocal}
              onChange={(e) => {
                const val = e.target.value; // 'paid' | 'unpaid' | 'processed'
                setStatusLocal(val);
                updateStatusInline(val);
              }}
              disabled={statusSaving}
              style={{
                marginLeft: 8,
                padding: '6px 10px',
                background: getStatusBackground(statusLocal),
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                cursor: statusSaving ? 'not-allowed' : 'pointer',
                opacity: statusSaving ? 0.7 : 1,
              }}
            >
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="processed">Processed</option>
            </select>
            {statusSaving && (
              <span style={{ marginLeft:10, color:'#aaa', fontSize:12 }}>Updating…</span>
            )}
          </div>

          <p style={{ margin:'5px 0', color:'#aaa' }}>
            <strong>Uploaded:</strong>{' '}
            {invoice.uploadDate
              ? new Date(invoice.uploadDate).toISOString().split('T')[0]
              : ''}
          </p>

          <div style={{ marginTop:20, display:'flex', gap:10 }}>
            <button onClick={handleDelete}
              style={{
                padding:'10px 20px', background:'#ea5a54', color:'#fff',
                border:'none', borderRadius:5, display:'flex', alignItems:'center', gap:6,
              }}
            >
              <img src={trashIcon} alt="Delete"
                    style={{ width:20, height:20, filter:'invert(1)' }} />
              Delete
            </button>
          </div>
        </div>
      ) : (
      /* EDIT MODE (unchanged) */
        <div style={{
          background:'#1e1e1e', padding:20, borderRadius:10,
          width:500, maxWidth:'90vw',
        }}>
          {/* description */}
          <label>Description</label>
          <input
            value={editingInvoice.description}
            onChange={e=>setEditingInvoice(prev=>({...prev,description:e.target.value}))}
            style={{
              width:'calc(100% - 20px)', padding:10,
              background:'#333', color:'#fff', border:'none',
              borderRadius:5, marginBottom:10,
            }}
          />

          {/* amount */}
          <label>Amount (€)</label>
          <input
            value={editingInvoice.amount}
            onChange={e=>setEditingInvoice(prev=>({...prev,amount:e.target.value}))}
            style={{
              width:'calc(100% - 20px)', padding:10,
              background:'#333', color:'#fff', border:'none',
              borderRadius:5, marginBottom:10,
            }}
          />

          {/* cost type */}
          <label>Cost Type</label>
          <select
            value={editingInvoice.costType}
            onChange={e=>setEditingInvoice(prev=>({...prev,costType:e.target.value}))}
            style={{
              width:'100%', padding:10, background:'#333', color:'#fff',
              border:'none', borderRadius:5, marginBottom:10,
            }}
          >
            <option value="">—</option>
            <option value="Recoupable">Recoupable</option>
            <option value="Recoupable (-Side Artists)">Recoupable (-Side Artists)</option>
            <option value="Non-Recoupable">Non-Recoupable</option>
            <option value="Advance">Advance</option>
          </select>

          {/* payment status */}
          <label>Payment Status</label>
          <select
            value={editingInvoice.paymentStatus}
            onChange={e=>setEditingInvoice(prev=>({...prev,paymentStatus:e.target.value}))}
            style={{
              width:'100%', padding:10, background:'#333', color:'#fff',
              border:'none', borderRadius:5, marginBottom:10,
            }}
          >
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="processed">Processed</option>
          </select>

          {/* PROJECT PICKER */}
          <label>Project</label>
          {!selectedProject ? (
            <>
              <input
                placeholder="Search project…"
                value={projectSearch}
                onChange={e=>setProjectSearch(e.target.value)}
                onFocus={()=>{setProjFocus(true); if(!projectSearch)setProjectSearch('');}}
                onBlur ={()=>setTimeout(()=>setProjFocus(false),100)}
                style={{
                  width:'calc(100% - 20px)', padding:10, background:'#333',
                  color:'#fff', border:'none', borderRadius:5, marginBottom:10,
                }}
              />
              {projFocus && filteredProjects.length>0 && (
                <div style={{
                  background:'#333', borderRadius:5, marginBottom:10,
                  maxHeight:160, overflowY:'auto',
                }}>
                  {filteredProjects.map(p=>(
                    <div key={p._id}
                      onMouseDown={()=>{
                        setSelectedProject(p); setProjectSearch(p.title);
                        setSelectedRelease(null); setReleaseSearch('');
                        setSelectedTask(null);   setTaskSearch('');
                        setProjFocus(false);
                      }}
                      style={{ padding:10, cursor:'pointer', borderBottom:'1px solid #555' }}
                    >{p.title}</div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{
              background:'#333', padding:10, borderRadius:5, marginBottom:10,
              display:'flex', justifyContent:'space-between', alignItems:'center',
            }}>
              <span>{selectedProject.title}</span>
              <button
                onClick={()=>{
                  setSelectedProject(null); setProjectSearch('');
                  setSelectedRelease(null); setReleaseSearch('');
                  setSelectedTask(null);   setTaskSearch('');
                }}
                style={{
                  padding:'5px 10px', background:'#555', color:'#fff',
                  border:'none', borderRadius:5,
                }}
              >Clear</button>
            </div>
          )}

          {/* RELEASE PICKER */}
          <label>Release</label>
          {!selectedRelease ? (
            <>
              <input
                placeholder="Search release…"
                value={releaseSearch}
                onChange={e=>setReleaseSearch(e.target.value)}
                onFocus={()=>{setRelFocus(true); if(!releaseSearch)setReleaseSearch('');}}
                onBlur ={()=>setTimeout(()=>setRelFocus(false),100)}
                style={{
                  width:'calc(100% - 20px)', padding:10, background:'#333',
                  color:'#fff', border:'none', borderRadius:5, marginBottom:10,
                }}
              />
              {relFocus && filteredReleases.length>0 && (
                <div style={{
                  background:'#333', borderRadius:5, marginBottom:10,
                  maxHeight:160, overflowY:'auto',
                }}>
                  {filteredReleases.map(r=>(
                    <div key={r._id}
                      onMouseDown={()=>{
                        setSelectedRelease(r); setReleaseSearch(r.title);
                        setSelectedTask(null); setTaskSearch('');
                        setRelFocus(false);
                      }}
                      style={{ padding:10, cursor:'pointer', borderBottom:'1px solid #555' }}
                    >{r.title}</div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{
              background:'#333', padding:10, borderRadius:5, marginBottom:10,
              display:'flex', justifyContent:'space-between', alignItems:'center',
            }}>
              <span>{selectedRelease.title}</span>
              <button
                onClick={()=>{
                  setSelectedRelease(null); setReleaseSearch('');
                  setSelectedTask(null); setTaskSearch('');
                }}
                style={{
                  padding:'5px 10px', background:'#555', color:'#fff',
                  border:'none', borderRadius:5,
                }}
              >Clear</button>
            </div>
          )}

          {/* TASK PICKER */}
          <label>Task</label>
          {!selectedTask ? (
            <>
              <input
                placeholder={loadingTasks?'Loading tasks…':'Search task…'}
                value={taskSearch}
                disabled={!selectedRelease || loadingTasks}
                onChange={e=>setTaskSearch(e.target.value)}
                onFocus={()=>{ setTaskFocus(true); if(!taskSearch)setTaskSearch(''); }}
                onBlur ={()=>setTimeout(()=>setTaskFocus(false),100)}
                style={{
                  width:'calc(100% - 20px)', padding:10, background:'#333',
                  color:'#fff', border:'none', borderRadius:5, marginBottom:10,
                }}
              />
              {taskFocus && filteredTasks.length>0 && (
                <div style={{
                  background:'#333', borderRadius:5, marginBottom:10,
                  maxHeight:160, overflowY:'auto',
                }}>
                  {filteredTasks.map(t=>(
                    <div key={t._id}
                      onMouseDown={()=>{
                        setSelectedTask(t); setTaskSearch(t.name);
                        setTaskFocus(false);
                      }}
                      style={{ padding:10, cursor:'pointer', borderBottom:'1px solid #555' }}
                    >{t.name}</div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{
              background:'#333', padding:10, borderRadius:5, marginBottom:10,
              display:'flex', justifyContent:'space-between', alignItems:'center',
            }}>
              <span>{selectedTask.name}</span>
              <button
                onClick={()=>{ setSelectedTask(null); setTaskSearch(''); }}
                style={{
                  padding:'5px 10px', background:'#555', color:'#fff',
                  border:'none', borderRadius:5,
                }}
              >Clear</button>
            </div>
          )}

          {/* replace pdf */}
          <div style={{ marginTop:20 }}>
            <label>Replace Invoice PDF (optional)</label>
            <input type="file" accept="application/pdf"
              onChange={handleNewFileChange}
              style={{
                width:'calc(100% - 20px)', padding:10, background:'#333',
                color:'#fff', border:'none', borderRadius:5, marginBottom:10,
              }}/>
            {newFile && (
              <button onClick={handleReplacePDF} disabled={uploadingFile}
                style={{
                  padding:'10px 20px', background:'#444', color:'#fff',
                  border:'none', borderRadius:5,
                }}
              >{uploadingFile?'Uploading…':'Replace PDF'}</button>
            )}
          </div>

          {/* buttons */}
          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button onClick={saveEditedInvoice}
              style={{
                padding:'10px 20px', background:'#28a745', color:'#fff',
                border:'none', borderRadius:5,
              }}
            >Save</button>
            <button onClick={handleCancelEdit}
              style={{
                padding:'10px 20px', background:'#555', color:'#fff',
                border:'none', borderRadius:5,
              }}
            >Cancel</button>
          </div>
        </div>
      )}

      {/* pdf preview */}
      {invoice.fileName ? (
        <div style={{ marginTop:40 }}>
          <h3 style={{ marginBottom:10 }}>PDF Preview</h3>
          <div style={{
            width:'60%', height:'1200px', background:'#333',
            borderRadius:5, overflow:'hidden',
          }}>
            <iframe
              title="Invoice PDF"
              src={`${BACKEND_URL}/uploads/${encodeURIComponent(invoice.fileName)}${pdfBust ? `?v=${pdfBust}` : ''}`}
              style={{ width:'100%', height:'100%', border:'none' }}
            />
          </div>
        </div>
      ) : (
        <p style={{ marginTop:20, color:'#999' }}>No PDF file attached</p>
      )}
    </div>
  );
}

export default InvoiceDetails;